import {useState, useEffect, useMemo, useRef, useCallback} from 'react';
import {Formik} from 'formik';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import ProcessPopup from '../../../../component/processPopup/ProcessPopup';
import * as css from './CreateSubscriptionPopup.css';
import FormikModeBtn from '../../../../component/formikModeBtn/FormikModeBtn';
import {useSubscriptionForm} from './hooks/useSubscriptionForm';
import {getSubscriptionStepButtons} from './subscriptionStepButtons';
import {useSubscriptionSteps} from './subscriptionSteps';
import useGlobalShow from '../../../../hooks/useGlobalShow';
import {setCurrentPlan} from '../../../../redux/subscription/subscriptionActions';
import SubscriptionOptions from '../../subscriptionsTable/subscriptionRow/SubscriptionOptions';
import setGlobalState from '../../../../redux/setGlobalState';
import {XHR_LOADING_STATE} from '../../../../assets/constants/constants';
import {SubscriptionFormValues} from './createSubscriptionForm.types';
import {TState} from '../../../../redux/store.type';
import {bounceMessageModal} from '../../../../layout/userMessages/UserMessage';
import {getPackageProducer} from '../../../../redux/account/accountActions';

function CreateSubscriptionPopup() {
    const {t} = useTranslation('subscriptions');
    const [currentStep, setCurrentStep] = useState(0);
    const show = useGlobalShow('isOpenCreateSubscription');
    const planTid = useSelector((state: TState) => state.subscription.currentPlan);
    const getPlanXHR = useSelector((state: TState) => state.subscription.getPlanXHR);
    const createPlanXHR = useSelector((state: TState) => state.subscription.createPlanXHR);
    const updatePlanXHR = useSelector((state: TState) => state.subscription.updatePlanXHR);
    const getPackageProducerXHR = useSelector((state: TState) => state.account.getPackageProducerXHR);
    const wasOpen = useRef(false);

    // Check if any XHR is loading
    const isLoading = getPlanXHR === XHR_LOADING_STATE ||
                     createPlanXHR === XHR_LOADING_STATE ||
                     updatePlanXHR === XHR_LOADING_STATE;

    const {validationSchema, getInitialValues, handleSubmit: handleFormSubmit} = useSubscriptionForm({planTid});
    const steps = useSubscriptionSteps();
    
    const initialValues = useMemo(() => getInitialValues(), [planTid, show.isOpen, getPlanXHR]);
    
    const resetState = () => {
        setGlobalState('subscription.currentPlan', null);
        setCurrentPlan(null);
        setCurrentStep(0);
    };
    
    // Reset state when popup closes (handles all close scenarios including click outside)
    useEffect(() => {
        if(wasOpen.current && !show.isOpen) {
            resetState();
        }
        wasOpen.current = show.isOpen;
    }, [show.isOpen]);
    
    const performClose = () => {
        show.close();
    };
    
    const createHandleClose = (form: any) => {
        return () => {
            const hasUnsavedChanges = Object.keys(form.touched).length > 0;
            
            if(hasUnsavedChanges) {
                const closeButton = {
                    text: t('close'),
                    onClick: () => {
                        performClose();
                    },
                    isImplementFromButtonOnly: true
                };
                
                bounceMessageModal(
                    t('unsavedChangesWarning'),
                    'warning',
                    {
                        text: t('continueEditing'),
                        onClick: () => {}
                    },
                    closeButton
                );
            } else {
                performClose();
            }
        };
    };

    const handleNext = () => {
        if(currentStep < steps.length - 1) {
            setCurrentStep(currentStep + 1);
        }
    };

    const handlePrevious = () => {
        if(currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };

    const handleStepClick = (stepIndex: number) => {
        if(!getPackageProducerXHR) {
            getPackageProducer().then();
        }
        setCurrentStep(stepIndex);
    };

    const handleSubmit = useCallback(async (values: SubscriptionFormValues, assignEvents: boolean = false) => {
        if(assignEvents) {
            setGlobalState('ui.show.isOpenAssigningEvents', true);
        }
        await handleFormSubmit(values);
        performClose();
    }, [handleFormSubmit]);

    const {getStepButtons, validateStep} = getSubscriptionStepButtons(
        currentStep,
        handleNext,
        handlePrevious,
        t,
        handleSubmit,
        !!planTid,
    );

    if(!show.isOpen) {
        return null;
    }

    return (
        <css.wrapper>
            <Formik
                initialValues={initialValues}
                enableReinitialize={true}
                validateOnMount={true}
                validationSchema={validationSchema}
                onSubmit={(values) => handleSubmit(values, false)}
            >
                {({handleSubmit: formikHandleSubmit, validateForm, setTouched, values, touched, isValid}) => (
                    <form onSubmit={formikHandleSubmit} noValidate>
                        <fieldset disabled={isLoading}>
                            <ProcessPopup
                                show={{...show, close: createHandleClose({touched})}}
                                title={t('createSubscriptionFormTitle')}
                                steps={steps}
                                currentStep={currentStep}
                                onStepClick={handleStepClick}
                                validateStep={(stepIndex) => validateStep(stepIndex, validateForm, setTouched)}
                                buttons={getStepButtons(validateForm, setTouched, values, isValid)}
                                headerItems={
                                    <css.headerItems>
                                        <FormikModeBtn name="active" className={''} forwardRef={undefined} />
                                        {planTid && <SubscriptionOptions plan={{planTid}}/>}
                                    </css.headerItems>
                                }
                                dataCyName="createSubscription"
                            />
                        </fieldset>
                    </form>
                )}
            </Formik>
        </css.wrapper>
    );
}

export default CreateSubscriptionPopup;

